import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  type IntensityGrid,
} from "@jackedlogs/shared";

import { IntensityGridView } from "../IntensityGrid/IntensityGridView";
import { PlacementMatrix, type PlacementMatrixProps } from "./PlacementMatrix";
import { useArmedConfirm } from "../primitives/useArmedConfirm";
import { useDisclosure } from "../primitives/useDisclosure";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import { CloneDayBar } from "./CloneDayBar";
import { CloneWeekBar } from "./CloneWeekBar";
import type { CloneDayResult, CloneWeekUiResult } from "./useComposer";

// The program's day×week navigation spine, rendered above the exercise list
// in the edit-in-place shell (collapsible on narrow, left rail on wide).
// It carries what the day list can't: the at-a-glance intensity grid (the
// whole program's shape), the PlacementMatrix (PE-22 F — the always-visible
// week×day cursor, and the open exercise's placement when there is one), and
// the ⧉ clone-day / clone-week composing actions.
//
// ⛔ Two controls that used to live here are gone, both absorbed by the matrix:
// the day-selector CHIP STRIP (the matrix is the cursor now) and the week
// STEPPER. The stepper could only go once the matrix carried the whole week
// axis — ＋ to add and a − on each row to drop THAT week, with the two-tap
// guard moved onto the row it acts on. Absorbing an additive control is a
// removal; absorbing a destructive one without replacing it would have been a
// capability hole, which is why the first pass kept the stepper.
// ⚠️ Its − was not even the same gesture: setCycleWeeks truncates the TAIL,
// while a row − drops the week you point at (removeWeekAt, PE-22 F).
export function ProgramSpine({
  grid,
  selectedDay,
  onSelectDay,
  selectedWeek,
  onSelectCell,
  placement,
  dayNames,
  onRemoveWeek,
  dark,
  wide,
  dayCount,
  weekCount,
  hasContent,
  resolveName,
  cloneDay,
  cloneWeek,
  addDay,
  removeDay,
  changeCycleWeeks,
  cs,
  ps,
}: {
  grid: IntensityGrid;
  selectedDay: number;
  onSelectDay: (day: number) => void;
  selectedWeek: number;
  onSelectCell: (dayIdx: number, week: number) => void;
  /** null while no exercise is open — the matrix is then a cursor only. */
  placement: PlacementMatrixProps["placement"];
  dayNames: string[];
  onRemoveWeek?: (week: number) => void;
  dark: boolean;
  wide: boolean;
  dayCount: number;
  weekCount: number;
  hasContent: boolean;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  cloneDay: (toDay: number) => CloneDayResult;
  cloneWeek: (fromWeek: number, toWeeks: number[]) => CloneWeekUiResult;
  // PE-11 — structural edits. Add a training day, remove one (guarded at 1),
  // resize the cycle. Absent → the controls hide (create-time / single-plan).
  addDay?: (name: string) => void;
  removeDay?: (idx: number) => void;
  changeCycleWeeks?: (weeks: number) => void;
  cs: ComposerStyles;
  ps: PlanStyles;
}) {
  const { t } = useTranslation();
  // Resolve the full theme locally for the shared primitives the matrix
  // mounts (the composer threads prebuilt `cs`/`ps` + `dark`, not a UiTheme)
  // — same resolution the composer container does.
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  // Grid is the at-a-glance overview: open by default on wide (the rail has
  // room), collapsed on narrow (the strip drives day switching). CN-15 —
  // swapped the hand-rolled useState+toggle for the shared disclosure
  // primitive (same fold state+a11y machine as the Home weekly-targets card).
  const { expanded: gridOpen, toggle: toggleGrid, a11yProps: gridA11y } =
    useDisclosure(wide);
  // Two-tap confirm on remove-day (it wipes a whole day) — the same
  // useArmedConfirm the exercise-remove uses, one level up in severity. The
  // week's own guard moved onto its matrix row with the − (PE-22 F).
  const [dayConfirm, setDayConfirm] = useArmedConfirm();
  const removeDayLabel = (armed: boolean) =>
    armed
      ? t("composer.removeDayConfirm", {
          n: selectedDay + 1,
          defaultValue: "Confirm — remove day D{{n}}?",
        })
      : t("composer.removeDay", {
          n: selectedDay + 1,
          defaultValue: "Remove day D{{n}}",
        });

  return (
    <View style={{ gap: 10 }}>
      <Pressable
        accessibilityRole="button"
        {...gridA11y}
        onPress={toggleGrid}
        style={ps.disclosureBtn}
      >
        <Text style={ps.disclosureText}>
          {gridOpen ? "▾" : "▸"}{" "}
          {t("composer.gridToggle", { defaultValue: "Intensity grid" })}
        </Text>
      </Pressable>
      {gridOpen ? (
        <IntensityGridView
          grid={grid}
          selected={selectedDay}
          onSelect={onSelectDay}
          dark={dark}
          title={null}
        />
      ) : null}

      <PlacementMatrix
        theme={theme}
        weekCount={weekCount}
        dayCount={dayCount}
        dayNames={dayNames}
        selectedDay={selectedDay}
        selectedWeek={selectedWeek}
        onSelectCell={onSelectCell}
        placement={placement}
        onRemoveWeek={changeCycleWeeks ? onRemoveWeek : undefined}
        onAddWeek={
          changeCycleWeeks && weekCount < MAX_CYCLE_WEEKS
            ? () => changeCycleWeeks(weekCount + 1)
            : undefined
        }
        onAddDay={
          addDay && dayCount < MAX_SESSIONS
            ? () => {
                addDay(`D${dayCount + 1}`);
                onSelectDay(dayCount);
              }
            : undefined
        }
      />

      {removeDay && dayCount > 1 ? (
        <Pressable
          onPress={() => {
            if (!dayConfirm) {
              setDayConfirm(true);
              return;
            }
            setDayConfirm(false);
            removeDay(selectedDay);
            onSelectDay(Math.max(0, selectedDay - 1));
          }}
          accessibilityRole="button"
          accessibilityLabel={removeDayLabel(dayConfirm)}
          hitSlop={8}
          style={[ps.removeBtn, { paddingHorizontal: 12, alignSelf: "flex-start" }]}
        >
          <Text style={ps.removeBtnText}>{removeDayLabel(dayConfirm)}</Text>
        </Pressable>
      ) : null}

      <CloneDayBar
        dayCount={dayCount}
        selectedDay={selectedDay}
        hasContent={hasContent}
        resolveName={resolveName}
        onClone={cloneDay}
        styles={cs}
      />
      <CloneWeekBar
        weekCount={weekCount}
        resolveName={resolveName}
        onClone={cloneWeek}
        styles={cs}
      />
    </View>
  );
}
